


import { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Grid, Card, CardContent, Chip, Avatar,
  TextField, Select, MenuItem, FormControl,
  InputLabel, Button, IconButton, Tab, Tabs, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel, CircularProgress, Alert,
  Tooltip, Skeleton, Fade, useMediaQuery, LinearProgress,
} from "@mui/material";
import { useTheme, createTheme, ThemeProvider } from "@mui/material/styles";
import ReplyIcon from "@mui/icons-material/Reply";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import VerifiedIcon from "@mui/icons-material/Verified";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";


// MUI THEME — Poppins everywhere, matching screenshot
const appTheme = createTheme({
  typography: {
    fontFamily: "sans-serif",
    allVariants: { fontFamily: "sans-serif" },
  },
  components: {
    MuiButton:    { styleOverrides: { root: { fontFamily: "sans-serif", textTransform: "none" } } },
    MuiTab:       { styleOverrides: { root: { fontFamily: "sans-serif", textTransform: "none" } } },
    MuiMenuItem:  { styleOverrides: { root: { fontFamily: "sans-serif" } } },
    MuiInputBase: { styleOverrides: { root: { fontFamily: "sans-serif" } } },
    MuiChip:      { styleOverrides: { root: { fontFamily: "sans-serif" } } },
  },
});

// DESIGN TOKENS — exact match to screenshot
const DRAWER_WIDTH = 280;

const T = {
  primary:        "#006D77",
  primaryLight:   "#e8f5f6",
  primaryMid:     "#b2d8db",
  positive:       "#16a34a",
  positiveBg:     "#f0fdf4",
  positiveBorder: "#bbf7d0",
  positiveChip:   "#dcfce7",
  negative:       "#dc2626",
  negativeBg:     "#fff1f2",
  negativeBorder: "#fecdd3",
  negativeChip:   "#fee2e2",
  warning:        "#d97706",
  warningBg:      "#fffbeb",
  warningBorder:  "#fde68a",
  border:         "#e5e7eb",
  cardBg:         "#ffffff",
  pageBg:         "#f9fafb",
  textPrimary:    "#111827",
  textSecondary:  "#6b7280",
  textMuted:      "#9ca3af",
  shadow:         "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd:       "0 4px 12px rgba(0,0,0,0.08)",
};

const SERVICE_LABELS = {
  medicine_delivery: "Medicine Delivery",
  diagnostic:        "Diagnostic",
  nursing:           "Nursing & Physio",
  package:           "Health Packages",
  camp:              "Health Camps",
};
const SERVICE_ICONS = {
  medicine_delivery: "💊", diagnostic: "🔬", nursing: "🩺", package: "📦", camp: "🏕️",
};
const SERVICES = Object.keys(SERVICE_LABELS);
const PER_PAGE = 10;

// API
const BASE = process.env.REACT_APP_API || "";
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
});
const api = {
  overview: ()         => fetch(`${BASE}/feedback/overview`,                                         { headers: authHeaders() }).then(r => r.json()),
  split:    (p)        => fetch(`${BASE}/feedback/split?${new URLSearchParams(p)}`,                  { headers: authHeaders() }).then(r => r.json()),
  all:      (p)        => fetch(`${BASE}/feedback/all?${new URLSearchParams(p)}`,                    { headers: authHeaders() }).then(r => r.json()),
  reply:    (id, body) => fetch(`${BASE}/feedback/${id}/reply`, { method: "PUT",    headers: authHeaders(), body: JSON.stringify(body) }).then(r => r.json()),
  delete:   (id)       => fetch(`${BASE}/feedback/${id}`,       { method: "DELETE", headers: authHeaders() }).then(r => r.json()),
  flag:     (id)       => fetch(`${BASE}/feedback/${id}/flag`,  { method: "PUT",    headers: authHeaders() }).then(r => r.json()),
};

// HELPERS
function pct(a, b) { return b ? Math.round((a / b) * 100) : 0; }
function buildParams(service, filters, extra = {}) {
  return {
    ...(service !== "all"   && { service_type: service }),
    ...(filters.search      && { search:       filters.search }),
    ...(filters.is_resolved && { is_resolved:  filters.is_resolved }),
    ...(filters.is_verified && { is_verified:  filters.is_verified }),
    ...(filters.date_from   && { date_from:    filters.date_from }),
    ...(filters.date_to     && { date_to:      filters.date_to }),
    ...extra,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// STAR RATING
// ─────────────────────────────────────────────────────────────────────────────
function StarRating({ rating, size = 16 }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
      {[1,2,3,4,5].map(s => {
        const Icon = rating >= s ? StarIcon : rating >= s - 0.5 ? StarHalfIcon : StarBorderIcon;
        return <Icon key={s} sx={{ fontSize: size, color: (rating >= s || rating >= s - 0.5) ? "#f59e0b" : "#d1d5db" }} />;
      })}
      <Typography sx={{ ml: 0.5, fontWeight: 600, color: T.textSecondary, fontSize: size - 3, fontFamily: "sans-serif" }}>
        {Number(rating).toFixed(1)}
      </Typography>
    </Box>
  );
}

// STAT CARD — matches screenshot card style exactly
function StatCard({ label, value, sub, color, borderTop, icon, loading }) {
  return (
    <Card elevation={0} sx={{
      border: `1px solid ${T.border}`, borderTop: `3px solid ${borderTop || color}`,
      borderRadius: 2, background: T.cardBg, height: "100%",
      boxShadow: T.shadow,
      transition: "box-shadow .2s",
      "&:hover": { boxShadow: T.shadowMd },
    }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
        {loading ? (
          <><Skeleton width="60%" height={36} /><Skeleton width="80%" height={20} sx={{ mt: 0.5 }} /></>
        ) : (
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: T.textSecondary, fontFamily: "sans-serif" }}>
                  {label}
                </Typography>
                <span style={{ fontSize: 15 }}>{typeof icon === "string" ? icon : ""}</span>
                {typeof icon !== "string" && icon}
              </Box>
              <Typography sx={{ fontSize: { xs: 26, sm: 30 }, fontWeight: 700, color, lineHeight: 1, fontFamily: "sans-serif" }}>
                {typeof value === "number" ? value.toLocaleString() : (value ?? "–")}
              </Typography>
              {sub && (
                <Typography sx={{ fontSize: 11, color: T.textMuted, mt: 0.5, fontFamily: "sans-serif" }}>
                  {sub}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// SERVICE PERFORMANCE
function ServicePerformance({ services, selectedService, onSelect, loading }) {
  return (
    <Card elevation={0} sx={{ border: `1px solid ${T.border}`, borderRadius: 2, mb: { xs: 2, sm: 3 }, boxShadow: T.shadow }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, letterSpacing: 0.5, mb: 2, textTransform: "uppercase", fontFamily: "sans-serif" }}>
          Service Performance · Click to Filter
        </Typography>
        <Grid container spacing={{ xs: 1, sm: 1.5 }}>
          {SERVICES.map(svc => {
            const s = services?.[svc];
            const posPct = s?.total ? pct(s.positive, s.total) : 0;
            const active = selectedService === svc;
            return (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={svc}>
                {loading ? (
                  <Skeleton height={90} variant="rectangular" sx={{ borderRadius: 2 }} />
                ) : (
                  <Box onClick={() => onSelect(active ? "all" : svc)} sx={{
                    p: 1.5, borderRadius: 2, cursor: "pointer",
                    border: `1.5px solid ${active ? T.primary : T.border}`,
                    background: active ? T.primaryLight : T.cardBg,
                    transition: "all .15s",
                    "&:hover": { border: `1.5px solid ${T.primary}`, background: T.primaryLight },
                  }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                        <span style={{ fontSize: 14 }}>{SERVICE_ICONS[svc]}</span>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "sans-serif" }}>
                          {SERVICE_LABELS[svc]}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", flexShrink: 0, ml: 0.5, fontFamily: "sans-serif" }}>
                        ★ {s?.avg_rating ?? "–"}
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={posPct} sx={{
                      height: 5, borderRadius: 3, mb: 0.75, backgroundColor: "#e5e7eb",
                      "& .MuiLinearProgress-bar": { background: `linear-gradient(90deg,${T.positive},${T.primary})`, borderRadius: 3 },
                    }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: 10, color: T.positive, fontWeight: 600, fontFamily: "sans-serif" }}>👍 {s?.positive || 0}</Typography>
                      <Typography sx={{ fontSize: 10, color: T.negative, fontWeight: 600, fontFamily: "sans-serif" }}>👎 {s?.negative || 0}</Typography>
                    </Box>
                  </Box>
                )}
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
}

// FEEDBACK CARD
function FeedbackCard({ fb, sentiment, onReply, onDelete, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const isPos  = sentiment === "positive";
  const accent = isPos ? T.positive : T.negative;
  const border = isPos ? T.positiveBorder : T.negativeBorder;
  const bg     = isPos ? T.positiveBg    : T.negativeBg;

  const handleFlag = async () => {
    try { const r = await api.flag(fb.feedback_id); if (r.success) onUpdated(r.feedback); } catch {}
  };

  return (
    <Card elevation={0} sx={{
      border: `1px solid ${border}`, borderLeft: `4px solid ${accent}`,
      borderRadius: 2, overflow: "hidden", height: "fit-content",
      boxShadow: T.shadow,
      transition: "box-shadow .2s, transform .15s",
      "&:hover": { boxShadow: T.shadowMd, transform: "translateY(-1px)" },
    }}>
      <CardContent sx={{ p: { xs: 1.75, sm: 2 }, "&:last-child": { pb: { xs: 1.75, sm: 2 } } }}>

        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.25, gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
            <Avatar sx={{
              width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 },
              bgcolor: bg, color: accent, fontSize: { xs: 13, sm: 14 }, fontWeight: 700,
              border: `1.5px solid ${border}`, flexShrink: 0,
              fontFamily: "sans-serif",
            }}>
              {(fb.is_anonymous ? "A" : fb.user_name?.[0] || "U").toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, color: T.textPrimary, fontSize: { xs: 12, sm: 13 }, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "sans-serif" }}>
                {fb.is_anonymous ? "Anonymous User" : fb.user_name}
              </Typography>
              {fb.user_email && !fb.is_anonymous && (
                <Typography sx={{ color: T.textMuted, fontSize: 10, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "sans-serif" }}>
                  {fb.user_email}
                </Typography>
              )}
              <Typography sx={{ color: T.textMuted, fontSize: 10, fontFamily: "sans-serif" }}>
                {new Date(fb.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
              </Typography>
            </Box>
          </Box>
          <Box sx={{
            width: 30, height: 30, borderRadius: "50%", background: bg,
            border: `1.5px solid ${border}`, display: "flex", alignItems: "center",
            justifyContent: "center", fontSize: 15, flexShrink: 0,
          }}>
            {isPos ? "😊" : "😞"}
          </Box>
        </Box>

        {/* Chips */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.25 }}>
          <Chip
            label={`${SERVICE_ICONS[fb.service_type] || "🏥"} ${fb.service_label || SERVICE_LABELS[fb.service_type]}`}
            size="small"
            sx={{ fontSize: 10, height: 20, background: T.primaryLight, color: T.primary, fontWeight: 600, fontFamily: "sans-serif" }}
          />
          {fb.is_verified && (
            <Chip icon={<VerifiedIcon sx={{ fontSize: "11px !important" }} />} label="Verified" size="small"
              sx={{ fontSize: 10, height: 20, background: "#dbeafe", color: "#1d4ed8", fontWeight: 600 }} />
          )}
          {fb.is_resolved
            ? <Chip icon={<CheckCircleIcon sx={{ fontSize: "11px !important" }} />} label="Resolved" size="small"
                sx={{ fontSize: 10, height: 20, background: T.positiveChip, color: T.positive, fontWeight: 600 }} />
            : <Chip icon={<PendingIcon sx={{ fontSize: "11px !important" }} />} label="Pending" size="small"
                sx={{ fontSize: 10, height: 20, background: "#fef3c7", color: "#92400e", fontWeight: 600 }} />
          }
          {fb.reference_id && (
            <Chip label={fb.reference_id} size="small"
              sx={{ fontSize: 10, height: 20, background: "#f3f4f6", color: T.textSecondary, fontWeight: 500, fontFamily: "monospace" }} />
          )}
        </Box>

        {/* Star rating */}
        <StarRating rating={fb.rating} />

        {/* Title */}
        {fb.title && (
          <Typography sx={{ fontWeight: 600, color: T.textPrimary, mt: 1, mb: 0.5, fontSize: { xs: 12, sm: 13 }, fontFamily: "sans-serif" }}>
            {fb.title}
          </Typography>
        )}

        {/* Comment */}
        <Typography sx={{
          color: T.textSecondary, lineHeight: 1.7, fontSize: { xs: 11, sm: 12 }, mt: 0.75, fontFamily: "sans-serif",
          display: expanded ? "block" : "-webkit-box",
          WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
          overflow: expanded ? "visible" : "hidden",
        }}>
          {fb.comment || "No comment provided."}
        </Typography>
        {fb.comment && fb.comment.length > 140 && (
          <Button size="small" onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            sx={{ mt: 0.25, p: 0, minWidth: 0, color: accent, fontSize: 11, textTransform: "none", fontWeight: 600, fontFamily: "sans-serif" }}>
            {expanded ? "Show less" : "Read more"}
          </Button>
        )}

        {/* Tags */}
        {fb.tags?.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1.25 }}>
            {fb.tags.map((t, i) => (
              <Chip key={i} label={t.label} size="small" sx={{
                fontSize: 10, height: 20, fontWeight: 600,
                fontFamily: "sans-serif",
                background: t.positive ? T.positiveChip : T.negativeChip,
                color:      t.positive ? T.positive      : T.negative,
                border:     `1px solid ${t.positive ? T.positiveBorder : T.negativeBorder}`,
              }} />
            ))}
          </Box>
        )}

        {/* Admin reply */}
        {fb.admin_reply && (
          <Box sx={{ mt: 1.5, background: T.primaryLight, borderLeft: `3px solid ${T.primary}`, borderRadius: "0 8px 8px 0", p: 1.25 }}>
            <Typography sx={{ color: T.primary, fontSize: 9, fontWeight: 700, display: "block", mb: 0.25, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: "sans-serif" }}>
              Admin Reply
            </Typography>
            <Typography sx={{ color: T.textSecondary, lineHeight: 1.6, display: "block", fontSize: 11, fontFamily: "sans-serif" }}>
              {fb.admin_reply}
            </Typography>
          </Box>
        )}

        {/* Footer */}
        <Divider sx={{ mt: 1.5, mb: 1.25, borderColor: T.border }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ color: T.textMuted, fontSize: 10, fontFamily: "monospace" }}>{fb.feedback_id}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <ThumbUpIcon sx={{ fontSize: 11, color: T.textMuted }} />
              <Typography sx={{ color: T.textMuted, fontSize: 10, fontFamily: "sans-serif" }}>{fb.helpful_count}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Tooltip title={fb.is_verified ? "Remove Verification" : "Verify"}>
              <IconButton size="small" onClick={handleFlag} sx={{
                width: 27, height: 27, borderRadius: 1.5,
                background: fb.is_verified ? "#dbeafe" : "#f3f4f6",
                color: fb.is_verified ? "#1d4ed8" : T.textMuted,
                border: `1px solid ${fb.is_verified ? "#bfdbfe" : T.border}`,
                "&:hover": { background: "#dbeafe", color: "#1d4ed8" },
              }}>
                <VerifiedIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reply">
              <IconButton size="small" onClick={() => onReply(fb)} sx={{
                width: 27, height: 27, borderRadius: 1.5,
                background: T.primaryLight, color: T.primary,
                border: `1px solid ${T.primaryMid}`,
                "&:hover": { background: T.primaryMid },
              }}>
                <ReplyIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => onDelete(fb.feedback_id)} sx={{
                width: 27, height: 27, borderRadius: 1.5,
                background: "#fff1f2", color: T.negative,
                border: `1px solid ${T.negativeBorder}`,
                "&:hover": { background: T.negativeChip },
              }}>
                <DeleteIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// FEEDBACK COLUMN
function FeedbackColumn({ title, icon, count, color, bg, border, feedbacks, loading, sentiment, onReply, onDelete, onUpdated, page, totalPages, onPageChange }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5, p: 1.5, background: bg, border: `1px solid ${border}`, borderRadius: 2, flexShrink: 0 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color, fontSize: { xs: 13, sm: 14 }, fontFamily: "sans-serif" }}>{title}</Typography>
          <Typography sx={{ color: T.textMuted, fontSize: 10, fontFamily: "sans-serif" }}>
            {count} reviews · Rating {sentiment === "positive" ? "≥ 3.5" : "< 3.5"}
          </Typography>
        </Box>
        <Box sx={{ px: 1.5, py: 0.4, borderRadius: 20, background: `${color}18`, border: `1px solid ${border}` }}>
          <Typography sx={{ fontWeight: 700, color, fontSize: 14, fontFamily: "sans-serif" }}>{count}</Typography>
        </Box>
      </Box>

      <Box sx={{
        flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.5, pb: 1, pr: 0.5,
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: "#d1d5db", borderRadius: 4 },
      }}>
        {loading
          ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={180} variant="rectangular" sx={{ borderRadius: 2 }} />)
          : feedbacks.length === 0
            ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography sx={{ fontSize: 32, mb: 1 }}>{sentiment === "positive" ? "🎉" : "✨"}</Typography>
                <Typography sx={{ color: T.textMuted, fontSize: 13, fontFamily: "sans-serif" }}>No {sentiment} feedback found</Typography>
              </Box>
            )
            : feedbacks.map(fb => (
              <FeedbackCard key={fb.feedback_id} fb={fb} sentiment={sentiment} onReply={onReply} onDelete={onDelete} onUpdated={onUpdated} />
            ))
        }
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 1.5, flexShrink: 0 }}>
          <Button size="small" onClick={() => onPageChange(page - 1)} disabled={page === 1} variant="outlined"
            sx={{ minWidth: 36, px: 1, borderRadius: 1.5, borderColor: T.border, color: T.primary, fontFamily: "sans-serif" }}>‹</Button>
          <Typography sx={{ color: T.textSecondary, fontSize: 12, fontFamily: "sans-serif" }}>{page} / {totalPages}</Typography>
          <Button size="small" onClick={() => onPageChange(page + 1)} disabled={page === totalPages} variant="outlined"
            sx={{ minWidth: 36, px: 1, borderRadius: 1.5, borderColor: T.border, color: T.primary, fontFamily: "sans-serif" }}>›</Button>
        </Box>
      )}
    </Box>
  );
}

// REPLY DIALOG
function ReplyDialog({ open, feedback, onClose, onSaved }) {
  const [reply, setReply]       = useState("");
  const [resolved, setResolved] = useState(false);
  const [verified, setVerified] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    if (feedback) { setReply(feedback.admin_reply || ""); setResolved(feedback.is_resolved || false); setVerified(feedback.is_verified || false); setError(""); }
  }, [feedback]);

  const handleSave = async () => {
    if (!reply.trim()) { setError("Reply cannot be empty."); return; }
    setSaving(true);
    try {
      const res = await api.reply(feedback.feedback_id, { admin_reply: reply.trim(), is_resolved: resolved, is_verified: verified });
      if (res.success) { onSaved(res.feedback); onClose(); }
      else setError(res.error || "Failed to save.");
    } catch { setError("Network error."); }
    finally { setSaving(false); }
  };

  if (!feedback) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, fontFamily: "sans-serif" } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Typography sx={{ fontWeight: 700, color: T.textPrimary, fontSize: { xs: 15, sm: 17 }, fontFamily: "sans-serif" }}>
          Reply to Feedback
        </Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ background: T.pageBg, border: `1px solid ${T.border}`, borderRadius: 2, p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: T.primaryLight, color: T.primary, fontSize: 12, fontFamily: "sans-serif" }}>
              {(feedback.user_name?.[0] || "U").toUpperCase()}
            </Avatar>
            <Typography sx={{ fontWeight: 600, color: T.textPrimary, fontSize: 13, fontFamily: "sans-serif" }}>
              {feedback.is_anonymous ? "Anonymous" : feedback.user_name}
            </Typography>
            <Chip label={SERVICE_LABELS[feedback.service_type]} size="small"
              sx={{ fontSize: 10, height: 20, background: T.primaryLight, color: T.primary }} />
            <StarRating rating={feedback.rating} />
          </Box>
          {feedback.title && (
            <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: 13, fontFamily: "sans-serif" }}>{feedback.title}</Typography>
          )}
          <Typography sx={{ color: T.textSecondary, lineHeight: 1.6, fontSize: 12, fontFamily: "sans-serif" }}>
            {feedback.comment || "No comment."}
          </Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        <TextField fullWidth multiline rows={4} label="Admin Reply" placeholder="Type your response to this feedback..."
          value={reply} onChange={e => { setReply(e.target.value); setError(""); }}
          inputProps={{ maxLength: 2000 }} helperText={`${reply.length}/2000`}
          sx={{
            mb: 2, fontFamily: "sans-serif",
            "& .MuiOutlinedInput-root": { borderRadius: 2, fontFamily: "sans-serif" },
            "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: T.primary },
            "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
          }}
        />
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <FormControlLabel control={<Checkbox checked={resolved} onChange={e => setResolved(e.target.checked)} sx={{ color: T.primary, "&.Mui-checked": { color: T.primary } }} />}
            label={<Typography sx={{ fontWeight: 600, fontSize: 13, fontFamily: "sans-serif" }}>Mark as Resolved</Typography>} />
          <FormControlLabel control={<Checkbox checked={verified} onChange={e => setVerified(e.target.checked)} sx={{ color: T.primary, "&.Mui-checked": { color: T.primary } }} />}
            label={<Typography sx={{ fontWeight: 600, fontSize: 13, fontFamily: "sans-serif" }}>Mark as Verified</Typography>} />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, borderColor: T.border, color: T.textSecondary, fontFamily: "sans-serif" }}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <ReplyIcon />}
          sx={{ borderRadius: 2, background: T.primary, "&:hover": { background: "#005a62" }, px: 3, fontFamily: "sans-serif" }}>
          {saving ? "Sending…" : "Send Reply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// DELETE DIALOG
function DeleteDialog({ open, feedbackId, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const go = async () => {
    setDeleting(true);
    try { const r = await api.delete(feedbackId); if (r.success) { onDeleted(feedbackId); onClose(); } } catch {}
    finally { setDeleting(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, color: T.textPrimary, fontFamily: "sans-serif" }}>Delete Feedback</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: T.textSecondary, fontSize: 13, fontFamily: "sans-serif" }}>
          Are you sure you want to permanently delete this feedback? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, borderColor: T.border, fontFamily: "sans-serif" }}>Cancel</Button>
        <Button onClick={go} variant="contained" color="error" disabled={deleting}
          startIcon={deleting ? <CircularProgress size={15} color="inherit" /> : <DeleteIcon />}
          sx={{ borderRadius: 2, fontFamily: "sans-serif" }}>
          {deleting ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// FILTERS BAR
function FiltersBar({ filters, onChange, onRefresh, loading }) {
  const [open, setOpen] = useState(false);
  const set = (k, v) => onChange({ ...filters, [k]: v });

  return (
    <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        {/* Sort select */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select value={filters.sort} onChange={e => set("sort", e.target.value)}
            sx={{ borderRadius: 1.5, fontSize: 13, background: "#fff", fontFamily: "sans-serif" }}>
            <MenuItem value="newest">Newest First</MenuItem>
            <MenuItem value="oldest">Oldest First</MenuItem>
            <MenuItem value="highest">Highest Rated</MenuItem>
            <MenuItem value="lowest">Lowest Rated</MenuItem>
            <MenuItem value="most_helpful">Most Helpful</MenuItem>
          </Select>
        </FormControl>

        {/* Filters toggle */}
        <Button variant="outlined" size="small"
          startIcon={<FilterListIcon fontSize="small" />}
          onClick={() => setOpen(!open)}
          sx={{
            borderRadius: 1.5, borderColor: open ? T.primary : T.border,
            color: open ? T.primary : T.textSecondary, fontSize: 13,
            background: "#fff", whiteSpace: "nowrap", fontFamily: "sans-serif",
          }}>
          Filters {open ? "▲" : "▼"}
        </Button>

        {/* Refresh — circle button like screenshot */}
        <Tooltip title="Refresh">
          <IconButton onClick={onRefresh} disabled={loading} sx={{
            width: 36, height: 36, borderRadius: "50%",
            background: T.primary, color: "#fff",
            "&:hover": { background: "#005a62" },
            "&:disabled": { background: T.border },
          }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {open && (
        <Fade in={open}>
          <Box sx={{ mt: 1.5, p: 2, background: "#fff", border: `1px solid ${T.border}`, borderRadius: 2, display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-end", boxShadow: T.shadow }}>
            <FormControl size="small" sx={{ minWidth: 148 }}>
              <InputLabel sx={{ fontSize: 13, fontFamily: "sans-serif" }}>Status</InputLabel>
              <Select value={filters.is_resolved} onChange={e => set("is_resolved", e.target.value)} label="Status"
                sx={{ borderRadius: 1.5, fontSize: 13, fontFamily: "sans-serif" }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="false">Pending</MenuItem>
                <MenuItem value="true">Resolved</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 148 }}>
              <InputLabel sx={{ fontSize: 13, fontFamily: "sans-serif" }}>Verification</InputLabel>
              <Select value={filters.is_verified} onChange={e => set("is_verified", e.target.value)} label="Verification"
                sx={{ borderRadius: 1.5, fontSize: 13, fontFamily: "sans-serif" }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Verified</MenuItem>
                <MenuItem value="false">Unverified</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="Date From" type="date" value={filters.date_from}
              onChange={e => set("date_from", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, fontFamily: "sans-serif" } }} />
            <TextField size="small" label="Date To" type="date" value={filters.date_to}
              onChange={e => set("date_to", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, fontFamily: "sans-serif" } }} />
            <Button size="small"
              onClick={() => onChange({ search: "", sort: "newest", is_resolved: "", is_verified: "", date_from: "", date_to: "" })}
              sx={{ color: T.negative, fontSize: 12, fontFamily: "sans-serif" }}>
              Clear All
            </Button>
          </Box>
        </Fade>
      )}
    </Box>
  );
}

// PAGINATION
function PaginationRow({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 3 }}>
      <Button onClick={() => onChange(page - 1)} disabled={page === 1} variant="outlined"
        sx={{ borderRadius: 1.5, borderColor: T.border, color: T.primary, fontFamily: "sans-serif" }}>
        ‹ Prev
      </Button>
      <Box sx={{ px: 2.5, py: 1, borderRadius: 1.5, border: `1px solid ${T.border}`, background: "#fff" }}>
        <Typography sx={{ color: T.textSecondary, fontSize: 13, fontFamily: "sans-serif" }}>
          Page {page} of {totalPages}
        </Typography>
      </Box>
      <Button onClick={() => onChange(page + 1)} disabled={page === totalPages} variant="outlined"
        sx={{ borderRadius: 1.5, borderColor: T.border, color: T.primary, fontFamily: "sans-serif" }}>
        Next ›
      </Button>
    </Box>
  );
}

// MAIN DASHBOARD
export default function AdminFeedbackDashboard() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [tab, setTab]             = useState(0);
  const [overview, setOverview]   = useState(null);
  const [split, setSplit]         = useState({ positive: { feedbacks: [], total: 0 }, negative: { feedbacks: [], total: 0 } });
  const [allData, setAllData]     = useState({ feedbacks: [], total: 0, positive_count: 0, negative_count: 0 });
  const [loadOv, setLoadOv]       = useState(true);
  const [loadSplit, setLoadSplit] = useState(true);
  const [loadAll, setLoadAll]     = useState(false);
  const [service, setService]     = useState("all");
  const [posPage, setPosPage]     = useState(1);
  const [negPage, setNegPage]     = useState(1);
  const [allPage, setAllPage]     = useState(1);
  const [replyFb, setReplyFb]     = useState(null);
  const [deleteId, setDeleteId]   = useState(null);
  const [filters, setFilters]     = useState({ search: "", sort: "newest", is_resolved: "", is_verified: "", date_from: "", date_to: "" });

  const fetchOv = useCallback(async () => {
    setLoadOv(true);
    try { const r = await api.overview(); if (r.success) setOverview(r); } catch {}
    finally { setLoadOv(false); }
  }, []);

  const fetchSplit = useCallback(async (svc, f, pp, np) => {
    setLoadSplit(true);
    try {
      const base = buildParams(svc, f);
      const [posR, negR] = await Promise.all([
        api.split({ ...base, sentiment: "positive", page: pp, per_page: PER_PAGE }),
        api.split({ ...base, sentiment: "negative", page: np, per_page: PER_PAGE }),
      ]);
      setSplit({
        positive: posR.success ? posR.positive : { feedbacks: [], total: 0 },
        negative: negR.success ? negR.negative : { feedbacks: [], total: 0 },
      });
    } catch {}
    finally { setLoadSplit(false); }
  }, []);

  const fetchAll = useCallback(async (svc, f, page) => {
    setLoadAll(true);
    try {
      const r = await api.all(buildParams(svc, f, { page, per_page: PER_PAGE, sort: f.sort }));
      if (r.success) setAllData(r);
    } catch {}
    finally { setLoadAll(false); }
  }, []);

  useEffect(() => { fetchOv(); }, [fetchOv]);
  useEffect(() => { if (tab < 3) fetchSplit(service, filters, posPage, negPage); }, [tab, service, filters, posPage, negPage, fetchSplit]);
  useEffect(() => { if (tab === 3) fetchAll(service, filters, allPage); }, [tab, service, filters, allPage, fetchAll]);
  useEffect(() => { setPosPage(1); setNegPage(1); setAllPage(1); }, [filters, service]);

  const refresh = () => {
    fetchOv();
    if (tab < 3) fetchSplit(service, filters, posPage, negPage);
    else fetchAll(service, filters, allPage);
  };

  const handleUpdated = (fb) => {
    const upd = list => list.map(f => f.feedback_id === fb.feedback_id ? fb : f);
    setSplit(p => ({ positive: { ...p.positive, feedbacks: upd(p.positive.feedbacks) }, negative: { ...p.negative, feedbacks: upd(p.negative.feedbacks) } }));
    setAllData(p => ({ ...p, feedbacks: upd(p.feedbacks) }));
  };

  const handleDeleted = (id) => {
    const rm = list => list.filter(f => f.feedback_id !== id);
    setSplit(p => ({
      positive: { ...p.positive, feedbacks: rm(p.positive.feedbacks), total: Math.max(0, p.positive.total - 1) },
      negative: { ...p.negative, feedbacks: rm(p.negative.feedbacks), total: Math.max(0, p.negative.total - 1) },
    }));
    setAllData(p => ({ ...p, feedbacks: rm(p.feedbacks), total: Math.max(0, p.total - 1) }));
    fetchOv();
  };

  const ov            = overview?.overall || {};
  const posTotalPages = Math.ceil((split.positive?.total || 0) / PER_PAGE);
  const negTotalPages = Math.ceil((split.negative?.total || 0) / PER_PAGE);
  const allTotalPages = Math.ceil((allData.total || 0) / PER_PAGE);

  const TABS = isMobile
    ? ["⚖️", "✅", "⚠️", "📋"]
    : ["Split View", "Positive", "Negative", "All Feedback"];

  return (
    <ThemeProvider theme={appTheme}>
      {/* Poppins from Google Fonts */}
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <Box sx={{
        minHeight: "100vh",
        background: T.pageBg,
        fontFamily: "sans-serif",
        ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
        boxSizing: "border-box",
        maxWidth: "100%",
        overflowX: "hidden",
      }}>

        {/* ── Page Header — matches screenshot exactly ── */}
        <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
            <Box>
              <Typography sx={{
                fontWeight: 700, color: T.primary, fontFamily: "sans-serif",
                fontSize: { xs: 24, sm: 28, md: 30 }, lineHeight: 1.3,
              }}>
                Feedback Dashboard
              </Typography>
              <Typography sx={{ color: T.textSecondary, mt: 0.25, fontSize: { xs: 16, sm: 15 }, fontFamily: "sans-serif" }}>
                Monitor and manage all customer feedback in real-time
              </Typography>
            </Box>
            {/* Refresh circle button like screenshot */}
            <Tooltip title="Refresh data">
              <IconButton onClick={refresh} disabled={loadOv || loadSplit} sx={{
                width: 42, height: 42, borderRadius: "50%",
                background: T.primary, color: "#fff",
                boxShadow: "0 2px 8px rgba(0,109,119,.3)",
                "&:hover": { background: "#005a62" },
              }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Stat Cards — same layout as screenshot ── */}
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 2.5, sm: 3 } }}>
          {[
            { label: "Total Feedback",   value: ov.total,      icon: "📊", color: T.primary,   borderTop: T.primary,   sub: "All time" },
            { label: "Positive Reviews", value: ov.positive,   icon: "✅", color: T.positive,  borderTop: T.positive,  sub: `${pct(ov.positive, ov.total)}% satisfaction` },
            { label: "Negative Reviews", value: ov.negative,   icon: "⚠️", color: T.negative,  borderTop: T.negative,  sub: `${pct(ov.negative, ov.total)}% dissatisfied` },
            { label: "Unresolved",       value: ov.unresolved, icon: "⏳", color: T.warning,   borderTop: T.warning,   sub: "Awaiting reply" },
          ].map((c, i) => (
            <Grid item xs={6} sm={6} md={3} key={i}>
              <StatCard {...c} loading={loadOv} />
            </Grid>
          ))}
        </Grid>

        {/* ── Service Performance ── */}
        <ServicePerformance services={overview?.services} selectedService={service} onSelect={setService} loading={loadOv} />

        {/* ── Tabs — same style as "Complaints | Feedback" in screenshot ── */}
        <Card elevation={0} sx={{ border: `1px solid ${T.border}`, borderRadius: 2, mb: { xs: 2, sm: 2.5 }, boxShadow: T.shadow, background: "#fff" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant={isMobile ? "fullWidth" : "standard"}
            sx={{
              px: { xs: 0, sm: 2 },
              "& .MuiTab-root": {
                fontWeight: 600, fontSize: { xs: 12, sm: 14 },
                textTransform: "none", color: T.textSecondary,
                minHeight: 52, fontFamily: "sans-serif",
              },
              "& .Mui-selected": { color: `${T.primary} !important`, fontWeight: 700 },
              "& .MuiTabs-indicator": { background: T.primary, height: 3, borderRadius: "3px 3px 0 0" },
              borderBottom: `1px solid ${T.border}`,
            }}
          >
            {TABS.map((label, i) => <Tab key={i} label={label} />)}
          </Tabs>
        </Card>

        {/* ── Filters ── */}
        <FiltersBar filters={filters} onChange={setFilters} onRefresh={refresh} loading={loadSplit || loadAll} />

        {/* ── SPLIT VIEW ── */}
        {tab === 0 && (
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ height: { xs: "auto", md: "calc(100vh - 500px)" }, minHeight: 380 }}>
                <FeedbackColumn
                  title="Positive Feedback" icon="✅" sentiment="positive"
                  color={T.positive} bg={T.positiveBg} border={T.positiveBorder}
                  count={split.positive?.total || 0} feedbacks={split.positive?.feedbacks || []}
                  loading={loadSplit} onReply={setReplyFb} onDelete={setDeleteId} onUpdated={handleUpdated}
                  page={posPage} totalPages={posTotalPages} onPageChange={setPosPage}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ height: { xs: "auto", md: "calc(100vh - 500px)" }, minHeight: 380 }}>
                <FeedbackColumn
                  title="Negative Feedback" icon="⚠️" sentiment="negative"
                  color={T.negative} bg={T.negativeBg} border={T.negativeBorder}
                  count={split.negative?.total || 0} feedbacks={split.negative?.feedbacks || []}
                  loading={loadSplit} onReply={setReplyFb} onDelete={setDeleteId} onUpdated={handleUpdated}
                  page={negPage} totalPages={negTotalPages} onPageChange={setNegPage}
                />
              </Box>
            </Grid>
          </Grid>
        )}

        {/* ── POSITIVE ONLY ── */}
        {tab === 1 && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2, p: 1.5, background: T.positiveBg, border: `1px solid ${T.positiveBorder}`, borderRadius: 2 }}>
              <span style={{ fontSize: 18 }}>✅</span>
              <Typography sx={{ fontWeight: 700, color: T.positive, fontSize: { xs: 13, sm: 14 }, fontFamily: "sans-serif" }}>
                Positive Feedback — {split.positive?.total || 0} reviews (Rating ≥ 3.5)
              </Typography>
            </Box>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {loadSplit
                ? Array.from({ length: 6 }).map((_, i) => <Grid item xs={12} sm={6} lg={4} key={i}><Skeleton height={200} variant="rectangular" sx={{ borderRadius: 2 }} /></Grid>)
                : (split.positive?.feedbacks || []).map(fb => (
                  <Grid item xs={12} sm={6} lg={4} key={fb.feedback_id}>
                    <FeedbackCard fb={fb} sentiment="positive" onReply={setReplyFb} onDelete={setDeleteId} onUpdated={handleUpdated} />
                  </Grid>
                ))
              }
            </Grid>
            <PaginationRow page={posPage} totalPages={posTotalPages} onChange={setPosPage} />
          </>
        )}

        {/* ── NEGATIVE ONLY ── */}
        {tab === 2 && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2, p: 1.5, background: T.negativeBg, border: `1px solid ${T.negativeBorder}`, borderRadius: 2 }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <Typography sx={{ fontWeight: 700, color: T.negative, fontSize: { xs: 13, sm: 14 }, fontFamily: "sans-serif" }}>
                Negative Feedback — {split.negative?.total || 0} reviews (Rating &lt; 3.5)
              </Typography>
            </Box>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {loadSplit
                ? Array.from({ length: 6 }).map((_, i) => <Grid item xs={12} sm={6} lg={4} key={i}><Skeleton height={200} variant="rectangular" sx={{ borderRadius: 2 }} /></Grid>)
                : (split.negative?.feedbacks || []).map(fb => (
                  <Grid item xs={12} sm={6} lg={4} key={fb.feedback_id}>
                    <FeedbackCard fb={fb} sentiment="negative" onReply={setReplyFb} onDelete={setDeleteId} onUpdated={handleUpdated} />
                  </Grid>
                ))
              }
            </Grid>
            <PaginationRow page={negPage} totalPages={negTotalPages} onChange={setNegPage} />
          </>
        )}

        {/* ── ALL ── */}
        {tab === 3 && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <Typography sx={{ color: T.textSecondary, fontSize: 13, fontFamily: "sans-serif" }}>
                {allData.total} total results
              </Typography>
              <Chip label={`👍 ${allData.positive_count} positive`} size="small"
                sx={{ background: T.positiveChip, color: T.positive, fontWeight: 600, fontSize: 11, fontFamily: "sans-serif" }} />
              <Chip label={`👎 ${allData.negative_count} negative`} size="small"
                sx={{ background: T.negativeChip, color: T.negative, fontWeight: 600, fontSize: 11, fontFamily: "sans-serif" }} />
            </Box>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {loadAll
                ? Array.from({ length: 9 }).map((_, i) => <Grid item xs={12} sm={6} lg={4} key={i}><Skeleton height={200} variant="rectangular" sx={{ borderRadius: 2 }} /></Grid>)
                : allData.feedbacks.length === 0
                  ? (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: "center", py: 8 }}>
                        <Typography sx={{ fontSize: 38, mb: 1 }}>🔍</Typography>
                        <Typography sx={{ color: T.textMuted, fontSize: 14, fontWeight: 500, fontFamily: "sans-serif" }}>No feedback found</Typography>
                        <Typography sx={{ color: T.textMuted, fontSize: 12, fontFamily: "sans-serif" }}>No feedback available for the selected filters</Typography>
                      </Box>
                    </Grid>
                  )
                  : allData.feedbacks.map(fb => (
                    <Grid item xs={12} sm={6} lg={4} key={fb.feedback_id}>
                      <FeedbackCard fb={fb} sentiment={fb.rating >= 3.5 ? "positive" : "negative"}
                        onReply={setReplyFb} onDelete={setDeleteId} onUpdated={handleUpdated} />
                    </Grid>
                  ))
              }
            </Grid>
            <PaginationRow page={allPage} totalPages={allTotalPages} onChange={setAllPage} />
          </>
        )}

        <Box sx={{ height: { xs: 24, md: 40 } }} />
      </Box>

      {/* Dialogs */}
      <ReplyDialog open={!!replyFb} feedback={replyFb} onClose={() => setReplyFb(null)} onSaved={handleUpdated} />
      <DeleteDialog open={!!deleteId} feedbackId={deleteId} onClose={() => setDeleteId(null)} onDeleted={handleDeleted} />
    </ThemeProvider>
  );
}